<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="!mergedConfig.hideLabel"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id,
        status
      }"
    />
    <Multiselect
      :id="id"
      ref="multiselect"
      :value="value"
      :customLabel="customLabel"
      :placeholder="disabled ? '—' : mergedConfig.placeholder || 'Type to search for departments'"
      :disabled="disabled"
      open-direction="bottom"
      :options="normalizedOptions"
      :loading="isLoading"
      :internal-search="false"
      :limit-text="limitText"
      :show-no-options="true"
      :show-no-results="true"
      :multiple="mergedConfig.multiple !== false"
      :max="mergedConfig.max"
      :data-test="dataTest"
      @input="update"
      @search-change="asyncFind"
    >
      <template slot="noOptions">
        Type to search for departments.
      </template>

      <template slot="noResult">
        No departments found.
      </template>
    </Multiselect>
  </div>
</template>

<script>
import FormMixin from './FormMixin'
import { mapGetters } from 'vuex'
import { getDepartments } from '@/api'
import _debounce from 'lodash/debounce'
import Multiselect from '@/components/Multiselect'
import { DEBOUNCE_INTERVAL } from '@/constants/'
import FormLabel from './FormLabel'

export default {
  mixins: [ FormMixin ],
  components: { Multiselect, FormLabel },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    allowedDepartmentIds: {
      type: Array,
      default: undefined,
    },
  },
  inject: {
    fetchOptionsOnMount: {
      type: Boolean,
      default: () => (false),
    },
    excludeOptions: {
      type: Array,
      default: () => ([]),
    },
    setAvailableOptions: {
      type: Function,
      default: () => (null),
    },
  },
  data () {
    return {
      departmentsData: {},
      isLoading: false,
      searchQuery: null,
    }
  },
  computed: {
    ...mapGetters('departments', [ 'departmentDisplayName' ]),
    ...mapGetters([ 'school' ]),
    searchConfig () {
      const config = {
        school: this.school,
        searchQuery: this.searchQuery,
        ...(this.mergedConfig.excludeHiddenInEvents ? { excludeHiddenInEvents: true } : {}),
        ...(this.mergedConfig.excludeHiddenInScheduling ? { excludeHiddenInScheduling: true } : {}),
      }
      return config
    },
    options () {
      return Object.keys(this.departmentsData)
    },
    mergedConfig () {
      return {
        type: 'text',
        multiple: true,
        excludeHiddenInEvents: false,
        excludeHiddenInScheduling: false,
        ...this.config,
      }
    },
    normalizedOptions () {
      let filteredOptions = this.options

      if (this.excludeOptions?.value) {
        filteredOptions = filteredOptions.filter(option => !this.excludeOptions.value.includes(option))
      }

      // If config.optionsFromAction is non-blank array then some action is triggered
      // and options are filtered based on actions
      const optionsFromAction = this.mergedConfig.optionsFromAction

      if (optionsFromAction && optionsFromAction.length > 0) {
        filteredOptions = filteredOptions.filter(option => optionsFromAction.includes(option))
      }

      if (this.allowedDepartmentIds) {
        filteredOptions = filteredOptions.filter(option => this.allowedDepartmentIds.includes(option))
      }

      return [ ...new Set(filteredOptions) ]
    },
  },
  watch: {
    normalizedOptions: {
      handler (options) {
        if (!this.setAvailableOptions || typeof this.setAvailableOptions !== 'function') { return }

        this.setAvailableOptions(options)
      },
    },
    allowedDepartmentIds: {
      handler (allowedDepartmentIds) {
        if (allowedDepartmentIds) {
          this.getDepartments()
        }
      },
    },
  },
  mounted () {
    if (this.fetchOptionsOnMount || this.allowedDepartmentIds) {
      this.getDepartments()
    }
  },
  methods: {
    getDept (id) {
      return this.departmentsData[id]
    },
    limitText (count) {
      return `and ${count} other departments`
    },
    customLabel (value) {
      const dept = this.getDept(value)
      return dept ? dept.displayName : this.departmentDisplayName(value) || value
    },
    update (value) {
      if (this.mergedConfig.multiple === false) {
        const department = this.getDept(value)
        if (department) {
          this.$store.commit('departments/setDepartment', { department })
        }
      } else {
        if (!value) {
          const department = this.getDept(value)
          if (department) {
            this.$store.commit('departments/setDepartment', { department })
          }
        } else {
          value.forEach(v => {
            const department = this.getDept(v)
            if (department) {
              this.$store.commit('departments/setDepartment', { department })
            }
          })
        }
      }
      this.$emit('input', value)
    },
    asyncFind (query) {
      if (this.allowedDepartmentIds) {
        return
      }

      if (query) {
        this.isLoading = true
        this.getDepartmentsDebounced(query)
      } else {
        this.isLoading = false
        this.departmentsData = {}
      }
    },
    getDepartmentsDebounced: _debounce(function (query) {
      this.getDepartments(query)
    }, DEBOUNCE_INTERVAL),
    async getDepartments (query) {
      this.searchQuery = query

      let { searchQuery, ...searchConfig } = this.searchConfig

      if (this.allowedDepartmentIds) {
        if (this.allowedDepartmentIds.length > 0) {
          searchConfig = {
            ...searchConfig,
            list: this.allowedDepartmentIds.join(','),
          }
        } else {
          this.departmentsData = {}
          this.isLoading = false
          return
        }
      }

      if (searchQuery) {
        searchConfig = { ...searchConfig, searchQuery }
      }

      const data = await getDepartments(searchConfig)

      this.departmentsData = data || {}
      this.isLoading = false
    },
  },
}
</script>

<style scoped>
.form-control {
  height: 36px;
}
.multiselect >>> .multiselect__tag {
  white-space: normal;
}
.multiselect >>> .multiselect__tags {
  overflow: hidden;
}
</style>
